import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// ── Carta 1: Fulmine — lampi randomici sullo schermo ──
const LightningEffect = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const drawBolt = (x1, y1, x2, y2, spread, depth) => {
      if (depth <= 0) return;
      const mx = (x1 + x2) / 2 + (Math.random() - 0.5) * spread;
      const my = (y1 + y2) / 2 + (Math.random() - 0.5) * spread;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(mx, my);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = `rgba(200,220,255,${0.6 + Math.random() * 0.4})`;
      ctx.lineWidth = depth * 1.5;
      ctx.shadowBlur = 15;
      ctx.shadowColor = '#a0c4ff';
      ctx.stroke();
      if (depth > 1) {
        drawBolt(x1, y1, mx, my, spread / 2, depth - 1);
        drawBolt(mx, my, x2, y2, spread / 2, depth - 1);
        if (Math.random() > 0.5) {
          const bx = mx + (Math.random() - 0.5) * spread * 2;
          const by = my + Math.random() * spread;
          drawBolt(mx, my, bx, by, spread / 2, depth - 2);
        }
      }
    };

    let frame = 0;
    const flashes = [0, 150, 320, 500, 700];
    const timers = flashes.map((delay) =>
      setTimeout(() => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // White flash overlay
        ctx.fillStyle = `rgba(180,200,255,${0.15 + Math.random() * 0.2})`;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // 2-4 bolts
        const count = 2 + Math.floor(Math.random() * 3);
        for (let i = 0; i < count; i++) {
          const sx = Math.random() * canvas.width;
          drawBolt(sx, 0, sx + (Math.random()-0.5)*200, canvas.height, 120, 5);
        }
        setTimeout(() => ctx.clearRect(0, 0, canvas.width, canvas.height), 80);
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
      className="fixed inset-0 z-[80] pointer-events-none"
    >
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      <motion.div
        className="absolute inset-0"
        animate={{ backgroundColor: ['rgba(100,140,255,0)', 'rgba(100,140,255,0.18)', 'rgba(100,140,255,0)', 'rgba(100,140,255,0.12)', 'rgba(100,140,255,0)'] }}
        transition={{ duration: 0.9, times: [0, 0.15, 0.35, 0.55, 1] }}
      />
    </motion.div>
  );
};

// ── Carta 2: Veleno — bagliore verde con gocce ──
// ── Carta 2: Veleno — tossico e malefico ──
const PoisonEffect = () => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden"
  >
    {/* Dark green sickly overlay pulsing */}
    <motion.div className="absolute inset-0"
      animate={{ opacity: [0, 0.65, 0.3, 0.55, 0.2, 0] }}
      transition={{ duration: 1.6, times: [0, 0.15, 0.35, 0.55, 0.8, 1] }}
      style={{ background: 'radial-gradient(ellipse at center, rgba(20,60,10,0.4) 0%, rgba(0,80,20,0.6) 60%, rgba(10,40,5,0.8) 100%)' }}
    />
    {/* Evil green vignette */}
    <motion.div className="absolute inset-0"
      animate={{ opacity: [0, 0.8, 0.4, 0] }}
      transition={{ duration: 1.5 }}
      style={{ boxShadow: 'inset 0 0 150px 60px rgba(0,120,30,0.65)' }}
    />
    {/* Skull-like dark center flash */}
    <motion.div className="absolute inset-0"
      animate={{ opacity: [0, 0.3, 0] }}
      transition={{ duration: 0.4, delay: 0.1 }}
      style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.5) 0%, transparent 60%)' }}
    />
    {/* Toxic bubbles rising */}
    {[...Array(20)].map((_, i) => {
      const size = 5 + Math.random() * 14;
      return (
        <motion.div key={i}
          style={{
            position:'absolute',
            left:`${5 + Math.random()*90}%`,
            bottom:`-${size}px`,
            width:size, height:size, borderRadius:'50%',
            background:`radial-gradient(circle at 35% 35%, rgba(100,220,80,0.9), rgba(0,120,20,0.7))`,
            boxShadow:`0 0 ${size/2}px rgba(50,200,50,0.6), inset 0 0 ${size/3}px rgba(0,60,0,0.4)`,
          }}
          animate={{ y:[0,-(200+Math.random()*300)], x:[(Math.random()-0.5)*60], opacity:[0,0.9,0.8,0], scale:[0.5,1,0.8] }}
          transition={{ duration:0.9+Math.random()*0.7, delay:Math.random()*0.5, ease:'easeOut' }}
        />
      );
    })}
    {/* Dripping poison from top */}
    {[...Array(8)].map((_,i) => (
      <motion.div key={"d"+i}
        style={{ position:'absolute', left:`${8+i*12}%`, top:0, width:4+Math.random()*6,
          background:`linear-gradient(to bottom,rgba(0,150,30,0.8),rgba(0,200,40,0.4),transparent)`,
          borderRadius:'0 0 50% 50%',
        }}
        animate={{ height:[0, 40+Math.random()*80], opacity:[0,0.9,0] }}
        transition={{ duration:0.6+Math.random()*0.4, delay:0.1+Math.random()*0.4 }}
      />
    ))}
  </motion.div>
);

// ── Carta 3: Fuoco/Drago — fiamme reali + effetto malus ──
const FireEffect = () => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: 0.15 }}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden"
  >
    {/* Dark red vignette flash */}
    <motion.div className="absolute inset-0"
      animate={{ opacity: [0, 0.9, 0.5, 0.8, 0] }}
      transition={{ duration: 1.4, times: [0, 0.1, 0.4, 0.65, 1] }}
      style={{ background: 'radial-gradient(ellipse at center, rgba(180,0,0,0.25) 0%, rgba(120,0,0,0.7) 100%)' }}
    />
    {/* Inset ember glow */}
    <motion.div className="absolute inset-0"
      animate={{ opacity: [0, 0.8, 0.3, 0.6, 0] }}
      transition={{ duration: 1.4 }}
      style={{ boxShadow: 'inset 0 0 120px 60px rgba(220,50,0,0.7)' }}
    />
    {/* Bottom fire base glow */}
    <motion.div className="absolute" style={{ bottom:0, left:0, right:0, height:'30%' }}
      animate={{ opacity:[0,0.9,0.5,0] }}
      transition={{ duration:1.2 }}
      style={{ background:'linear-gradient(to top,rgba(255,80,0,0.6),rgba(220,30,0,0.3),transparent)', bottom:0, left:0, right:0, height:'30%', position:'absolute' }}
    />
    {/* Tall flames rising from bottom */}
    {[...Array(22)].map((_, i) => {
      const x = 2 + (i / 22) * 96;
      const h = 60 + Math.random() * 160;
      const w = 8 + Math.random() * 20;
      const dur = 0.4 + Math.random() * 0.5;
      const delay = Math.random() * 0.5;
      const color1 = `rgba(255,${140+Math.floor(Math.random()*80)},0,1)`;
      const color2 = `rgba(220,${40+Math.floor(Math.random()*60)},0,0.7)`;
      return (
        <motion.div key={i}
          style={{
            position:'absolute', left:`${x}%`, bottom:0,
            width:w, height:h,
            borderRadius:'60% 60% 30% 30% / 80% 80% 20% 20%',
            background:`linear-gradient(to top,${color1},${color2},rgba(255,80,0,0.2),transparent)`,
            filter:'blur(3px)',
            transformOrigin:'bottom center',
          }}
          animate={{
            y:[0, -(h*0.6 + Math.random()*40)],
            scaleX:[1, 0.3+Math.random()*0.4],
            scaleY:[1, 1.2, 0.8],
            opacity:[0, 1, 0.8, 0],
            rotate:[(Math.random()-0.5)*10, (Math.random()-0.5)*20],
          }}
          transition={{ duration:dur, delay, ease:'easeOut' }}
        />
      );
    })}
    {/* Embers sparks */}
    {[...Array(18)].map((_,i) => (
      <motion.div key={"e"+i}
        style={{ position:'absolute', left:`${5+Math.random()*90}%`, bottom:`${Math.random()*20}%`,
          width:3+Math.random()*4, height:3+Math.random()*4, borderRadius:'50%',
          background:`rgba(255,${180+Math.floor(Math.random()*75)},0,1)`,
          boxShadow:`0 0 6px rgba(255,100,0,0.8)`,
        }}
        animate={{ y:[0,-(100+Math.random()*200)], x:[(Math.random()-0.5)*30], opacity:[0,1,1,0] }}
        transition={{ duration:0.8+Math.random()*0.5, delay:Math.random()*0.4, ease:'easeOut' }}
      />
    ))}
  </motion.div>
);

// ── Carta 8/9: Corona volante verso giocatore prev/next ──
const FlyingCrownEffect = ({ direction }) => {
  // direction: 'prev' = va in alto-sinistra, 'next' = va in alto-destra
  const toX = direction === 'prev' ? '-40vw' : '40vw';
  const toY = '-35vh';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] pointer-events-none flex items-center justify-center"
    >
      {/* Subtle directional glow */}
      <motion.div
        className="absolute inset-0"
        animate={{ opacity: [0, 0.3, 0] }}
        transition={{ duration: 1.6 }}
        style={{
          background: direction === 'prev'
            ? 'linear-gradient(to top-left, transparent, rgba(251,191,36,0.2))'
            : 'linear-gradient(to top-right, transparent, rgba(251,191,36,0.2))',
        }}
      />
      {/* Flying crown */}
      <motion.div
        initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
        animate={{ x: toX, y: toY, scale: [1, 1.6, 0.8], opacity: [1, 1, 0] }}
        transition={{ duration: 1.5, ease: 'easeInOut' }}
        style={{ position: 'absolute' }}
      >
        {/* Trail */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: 'translate(-50%,-50%)',
              opacity: 0.15 - i * 0.02,
              scale: 1 - i * 0.15,
            }}
          >
            <CrownSVG size={56} />
          </motion.div>
        ))}
        <motion.div
          animate={{ rotate: [0, -8, 8, -4, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 0.4, repeat: 3 }}
        >
          <CrownSVG size={72} />
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// ── Carta 10: Gold — bagliore giallo + corona pulsante ──
const GoldEffect = () => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
    className="fixed inset-0 z-[80] pointer-events-none flex items-center justify-center"
  >
    {/* Yellow full-screen glow */}
    <motion.div
      className="absolute inset-0"
      animate={{ opacity: [0, 0.75, 0.35, 0.6, 0] }}
      transition={{ duration: 1.3, times: [0, 0.2, 0.45, 0.65, 1] }}
      style={{ background: 'radial-gradient(ellipse at center, rgba(251,191,36,0.55) 0%, rgba(217,119,6,0.25) 60%, transparent 100%)' }}
    />
    <motion.div
      className="absolute inset-0"
      animate={{ opacity: [0, 0.5, 0] }}
      transition={{ duration: 1.3 }}
      style={{ boxShadow: 'inset 0 0 120px 40px rgba(251,191,36,0.5)' }}
    />
    {/* Pulsing crown in center */}
    <motion.div
      animate={{ scale: [0.5, 1.8, 1.4, 2.0, 0], opacity: [0, 1, 1, 0.8, 0] }}
      transition={{ duration: 1.3, times: [0, 0.25, 0.5, 0.75, 1], ease: 'easeOut' }}
      style={{ position: 'relative', zIndex: 10 }}
    >
      <motion.div
        animate={{ rotate: [-5, 5, -5, 5, 0] }}
        transition={{ duration: 0.3, repeat: 3 }}
      >
        <CrownSVG size={120} />
      </motion.div>
    </motion.div>
    {/* Sparkle particles */}
    {[...Array(20)].map((_, i) => {
      const angle = (i / 20) * Math.PI * 2;
      const dist = 100 + Math.random() * 150;
      return (
        <motion.div
          key={i}
          style={{ position: 'absolute', width: 4, height: 4, borderRadius: '50%', background: '#fbbf24', boxShadow: '0 0 6px #fbbf24' }}
          animate={{
            x: [0, Math.cos(angle) * dist],
            y: [0, Math.sin(angle) * dist],
            opacity: [0, 1, 0],
            scale: [0, 1.5, 0],
          }}
          transition={{ duration: 0.8 + Math.random() * 0.4, delay: Math.random() * 0.3 }}
        />
      );
    })}
  </motion.div>
);

// Crown SVG helper
const CrownSVG = ({ size }) => (
  <svg width={size} height={size * 0.7} viewBox="0 0 48 34" fill="none">
    <defs>
      <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fde68a" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
    </defs>
    <path d="M4 30h40v4H4zM4 26L8 8l8 10L24 2l8 16 8-10 4 18H4z" fill="url(#cg)" />
    <circle cx="24" cy="4" r="3" fill="#ef4444" />
    <circle cx="8" cy="10" r="2.5" fill="#ef4444" />
    <circle cx="40" cy="10" r="2.5" fill="#ef4444" />
  </svg>
);


// ── Jolly: pioggia di coriandoli + cappello centrale pulsante ──
const JollyScreenEffect = () => {
  const colors = ['#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#a855f7','#ec4899','#fbbf24','#06b6d4'];
  const confetti = Array.from({ length: 60 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 0.6,
    duration: 1.0 + Math.random() * 0.8,
    size: 6 + Math.random() * 10,
    color: colors[Math.floor(Math.random() * colors.length)],
    rotate: Math.random() * 720 - 360,
    shape: Math.random() > 0.5 ? 'rect' : 'circle',
    wobble: (Math.random() - 0.5) * 60,
  }));

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[80] pointer-events-none overflow-hidden flex items-center justify-center"
    >
      {/* Coriandoli */}
      {confetti.map(({ id, x, delay, duration, size, color, rotate, shape, wobble }) => (
        <motion.div
          key={id}
          style={{
            position: 'absolute',
            left: `${x}%`,
            top: -20,
            width: shape === 'rect' ? size : size,
            height: shape === 'rect' ? size * 0.45 : size,
            borderRadius: shape === 'circle' ? '50%' : '2px',
            background: color,
            boxShadow: `0 0 4px ${color}88`,
          }}
          animate={{
            y: ['0vh', '110vh'],
            rotate: [0, rotate],
            x: [0, wobble],
            opacity: [0, 1, 1, 0],
          }}
          transition={{
            duration,
            delay,
            ease: 'easeIn',
            opacity: { times: [0, 0.1, 0.8, 1] },
          }}
        />
      ))}

      {/* Cappello da giullare centrale */}
      <motion.div
        animate={{
          scale: [0, 1.4, 1.1, 1.5, 0.9, 1.3, 0],
          opacity: [0, 1, 1, 1, 1, 1, 0],
          rotate: [-15, 10, -8, 12, -5, 8, 0],
        }}
        transition={{ duration: 2.0, times: [0, 0.15, 0.35, 0.5, 0.65, 0.8, 1], ease: 'easeInOut' }}
        style={{ position: 'relative', zIndex: 10, filter: 'drop-shadow(0 0 20px rgba(251,191,36,0.8))' }}
      >
        <img
          src="/images/icons/jolly.png"
          alt="Jolly"
          style={{ width: 140, height: 140, objectFit: 'contain' }}
          draggable={false}
        />
      </motion.div>

      {/* Bagliore multicolore dietro il cappello */}
      <motion.div
        animate={{ opacity: [0, 0.6, 0.3, 0.5, 0], scale: [0.5, 2, 1.5, 2.2, 0] }}
        transition={{ duration: 2.0 }}
        style={{
          position: 'absolute',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'conic-gradient(#ef4444, #f97316, #eab308, #22c55e, #3b82f6, #a855f7, #ef4444)',
          filter: 'blur(40px)',
          zIndex: 5,
        }}
      />
    </motion.div>
  );
};


// ── Carta 4: Luccichii ──
const SparkleEffect = () => (
  <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
    className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
    {[...Array(28)].map((_,i) => {
      const x = 5 + Math.random()*90;
      const y = 5 + Math.random()*90;
      const delay = Math.random()*0.7;
      const size = 8 + Math.random()*14;
      return (
        <motion.div key={i}
          style={{ position:'absolute', left:`${x}%`, top:`${y}%`, width:size, height:size, pointerEvents:'none' }}
          initial={{ scale:0, opacity:0 }}
          animate={{ scale:[0,1.4,0.8,1.2,0], opacity:[0,1,0.8,1,0], rotate:[0,180,360] }}
          transition={{ duration:1+Math.random()*0.4, delay, ease:'easeInOut' }}
        >
          <svg viewBox="0 0 24 24" fill="none" style={{ width:'100%', height:'100%' }}>
            <path d="M12 2 L13.5 9 L20 12 L13.5 15 L12 22 L10.5 15 L4 12 L10.5 9 Z"
              fill={['#ffd700','#fff9c4','#ffe066','#fff'][i%4]}
              filter="url(#sparkle-blur)"
            />
          </svg>
        </motion.div>
      );
    })}
    {/* Subtle golden wash */}
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.12,0.06,0.1,0] }}
      transition={{ duration:1.1 }}
      style={{ background:'radial-gradient(ellipse at center,rgba(255,230,100,0.3),transparent 70%)' }}
    />
  </motion.div>
);

// ── Carta 5: Petali/fiori che cadono ──
const PetalEffect = () => {
  const petals = Array.from({length:22},(_,i) => ({
    x: 2 + Math.random()*96,
    delay: Math.random()*0.6,
    dur: 1.4+Math.random()*0.8,
    size: 14+Math.random()*12,
    color: ['#f9a8d4','#fbcfe8','#fce7f3','#f472b6','#ec4899'][i%5],
    wobble: (Math.random()-0.5)*80,
    rot: Math.random()*360,
  }));
  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
      {/* Soft green wash */}
      <motion.div className="absolute inset-0"
        animate={{ opacity:[0,0.18,0.1,0] }} transition={{ duration:1.3 }}
        style={{ background:'radial-gradient(ellipse at center,rgba(134,239,172,0.2),transparent 70%)' }}
      />
      {petals.map((p,i) => (
        <motion.div key={i}
          style={{ position:'absolute', left:`${p.x}%`, top:-30, pointerEvents:'none' }}
          animate={{ y:['0vh','115vh'], x:[0,p.wobble], rotate:[p.rot,p.rot+240], opacity:[0,1,1,0] }}
          transition={{ duration:p.dur, delay:p.delay, ease:'easeIn' }}
        >
          <svg width={p.size} height={p.size} viewBox="0 0 24 24">
            <ellipse cx="12" cy="6" rx="5" ry="8" fill={p.color} opacity="0.88" transform="rotate(0 12 12)"/>
            <ellipse cx="12" cy="6" rx="5" ry="8" fill={p.color} opacity="0.7" transform="rotate(72 12 12)"/>
            <ellipse cx="12" cy="6" rx="5" ry="8" fill={p.color} opacity="0.7" transform="rotate(144 12 12)"/>
            <ellipse cx="12" cy="6" rx="5" ry="8" fill={p.color} opacity="0.7" transform="rotate(216 12 12)"/>
            <ellipse cx="12" cy="6" rx="5" ry="8" fill={p.color} opacity="0.7" transform="rotate(288 12 12)"/>
            <circle cx="12" cy="12" r="3" fill="#fef08a"/>
          </svg>
        </motion.div>
      ))}
    </motion.div>
  );
};

// ── Carta 6: Farfalle blu ──
const ButterflyEffect = () => {
  const butterflies = Array.from({length:12},(_,i)=>({
    startX: 5+Math.random()*90,
    startY: 20+Math.random()*60,
    delay: Math.random()*0.6,
    color: ['#60a5fa','#3b82f6','#93c5fd','#bfdbfe','#2563eb'][i%5],
  }));
  return (
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[80] pointer-events-none overflow-hidden">
      {/* Soft blue wash */}
      <motion.div className="absolute inset-0"
        animate={{ opacity:[0,0.15,0.08,0] }} transition={{ duration:1.3 }}
        style={{ background:'radial-gradient(ellipse at center,rgba(96,165,250,0.2),transparent 70%)' }}
      />
      {butterflies.map((b,i) => (
        <motion.div key={i}
          style={{ position:'absolute', left:`${b.startX}%`, top:`${b.startY}%` }}
          animate={{
            x: [(Math.random()-0.5)*40,(Math.random()-0.5)*120,(Math.random()-0.5)*200],
            y: [0,-(30+Math.random()*60),-(60+Math.random()*100)],
            opacity:[0,1,1,0],
          }}
          transition={{ duration:1.6+Math.random()*0.6, delay:b.delay, ease:'easeInOut' }}
        >
          {/* Wing flutter */}
          <motion.div
            animate={{ scaleX:[1,0.3,1,0.3,1] }}
            transition={{ duration:0.3, repeat:Infinity, delay:b.delay }}
            style={{ display:'flex', alignItems:'center' }}
          >
            <svg width="28" height="20" viewBox="0 0 40 28">
              {/* Left wings */}
              <ellipse cx="10" cy="10" rx="10" ry="7" fill={b.color} opacity="0.85"/>
              <ellipse cx="8" cy="20" rx="8" ry="6" fill={b.color} opacity="0.7"/>
              {/* Right wings */}
              <ellipse cx="30" cy="10" rx="10" ry="7" fill={b.color} opacity="0.85"/>
              <ellipse cx="32" cy="20" rx="8" ry="6" fill={b.color} opacity="0.7"/>
              {/* Body */}
              <ellipse cx="20" cy="14" rx="2" ry="7" fill="#1e3a5f"/>
              {/* Antennae */}
              <line x1="19" y1="7" x2="14" y2="2" stroke="#1e3a5f" strokeWidth="1"/>
              <line x1="21" y1="7" x2="26" y2="2" stroke="#1e3a5f" strokeWidth="1"/>
              <circle cx="14" cy="2" r="1.5" fill="#93c5fd"/>
              <circle cx="26" cy="2" r="1.5" fill="#93c5fd"/>
            </svg>
          </motion.div>
        </motion.div>
      ))}
    </motion.div>
  );
};


// ── Carta 7: Scudo pulsante ──
const ShieldEffect = () => (
  <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
    className="fixed inset-0 z-[80] pointer-events-none flex items-center justify-center">
    {/* Subtle blue wash */}
    <motion.div className="absolute inset-0"
      animate={{ opacity:[0,0.14,0.06,0] }} transition={{ duration:1.4 }}
      style={{ background:'radial-gradient(ellipse at center,rgba(96,165,250,0.25),transparent 65%)' }}
    />
    {/* Ripple rings */}
    {[0,1,2].map(i => (
      <motion.div key={i}
        style={{ position:'absolute', borderRadius:'50%', border:'2px solid rgba(96,165,250,0.6)' }}
        initial={{ width:80, height:80, opacity:0.8 }}
        animate={{ width:[80,320], height:[80,320], opacity:[0.7,0] }}
        transition={{ duration:1.1, delay:i*0.28, ease:'easeOut' }}
      />
    ))}
    {/* Central shield SVG */}
    <motion.div
      animate={{ scale:[0.6,1.25,1.1,1.2,1], opacity:[0,1,0.9,1,0] }}
      transition={{ duration:1.3, ease:'easeOut' }}
      style={{ position:'relative', zIndex:2 }}
    >
      <svg width="110" height="128" viewBox="0 0 60 70">
        <defs>
          <linearGradient id="shg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#93c5fd"/>
            <stop offset="50%" stopColor="#3b82f6"/>
            <stop offset="100%" stopColor="#1d4ed8"/>
          </linearGradient>
          <filter id="shglow"><feGaussianBlur stdDeviation="3" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <path d="M4 4 L56 4 L56 36 Q56 56 30 66 Q4 56 4 36 Z" fill="url(#shg)" filter="url(#shglow)" stroke="rgba(147,197,253,0.8)" strokeWidth="2"/>
        {/* Cross emblem */}
        <rect x="26" y="14" width="8" height="28" rx="2" fill="rgba(255,255,255,0.85)"/>
        <rect x="14" y="26" width="32" height="8" rx="2" fill="rgba(255,255,255,0.85)"/>
        {/* Shine */}
        <path d="M10 10 Q20 8 28 12" stroke="rgba(255,255,255,0.5)" strokeWidth="2" fill="none" strokeLinecap="round"/>
      </svg>
    </motion.div>
  </motion.div>
);

// ── Main overlay dispatcher ──
const CardEffectOverlay = ({ effect }) => (
  <AnimatePresence>
    {effect === 'lightning'   && <LightningEffect    key="lightning" />}
    {effect === 'poison'      && <PoisonEffect      key="poison" />}
    {effect === 'fire'        && <FireEffect         key="fire" />}
    {effect === 'crown_prev'  && <FlyingCrownEffect  key="crown_prev" direction="prev" />}
    {effect === 'crown_next'  && <FlyingCrownEffect  key="crown_next" direction="next" />}
    {effect === 'gold'        && <GoldEffect         key="gold" />}
    {effect === 'jolly_effect' && <JollyScreenEffect key="jolly_effect" />}
    {effect === 'sparkle'     && <SparkleEffect      key="sparkle" />}
    {effect === 'shield'      && <ShieldEffect       key="shield" />}
    {effect === 'petals'      && <PetalEffect        key="petals" />}
    {effect === 'butterflies' && <ButterflyEffect    key="butterflies" />}
  </AnimatePresence>
);

export default CardEffectOverlay;
